// frontend/src/App.jsx
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { PLAN_LIMIT_EVENT } from "./api/client";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import SettingsLayout from "./components/SettingsLayout";
import UpgradeModal from "./components/UpgradeModal";
import LoginPage from "./pages/LoginPage";
import MfaChallengePage from "./pages/MfaChallengePage";
import RegisterPage from "./pages/RegisterPage";
import PrivacyPage from "./pages/PrivacyPage";
import DashboardPage from "./pages/DashboardPage";
import CodesPage from "./pages/CodesPage";
import CodeDetailPage from "./pages/CodeDetailPage";
import BatchPage from "./pages/BatchPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import OrgSettingsPage from "./pages/OrgSettingsPage";
import ApiKeysPage from "./pages/ApiKeysPage";
import BillingPage from "./pages/BillingPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ExportPage from "./pages/ExportPage";
import NotificationPrefsPage from "./pages/NotificationPrefsPage";
import WebhooksPage from "./pages/WebhooksPage";
import TicketsPage from "./pages/TicketsPage";
import TicketDetailPage from "./pages/TicketDetailPage";
import AdminApp from "./admin/App";
import ImpersonationBanner from "./components/ImpersonationBanner";
import CookieBanner from "./components/CookieBanner";
import PublicLayout from "./marketing/PublicLayout";
import HomeGate from "./pages/public/HomeGate";
import PricingPage from "./pages/public/PricingPage";
import DocsLayout from "./pages/public/DocsLayout";
import DocsHub from "./pages/public/DocsHub";
import DocPage from "./pages/public/DocPage";
import SolutionsIndex from "./pages/public/SolutionsIndex";
import SolutionTemplate from "./pages/public/SolutionTemplate";
import LegalLayout from "./pages/public/LegalLayout";
import LegalIndex from "./pages/public/LegalIndex";
import LegalPage from "./pages/public/LegalPage";
import SecurityPage from "./pages/public/SecurityPage";
import FeaturesPage from "./pages/public/FeaturesPage";
import QrTypesPage from "./pages/public/QrTypesPage";
import AboutPage from "./pages/public/AboutPage";
import ContactPage from "./pages/public/ContactPage";
import NotFoundPage from "./pages/public/NotFoundPage";

/** Map the backend's machine feature names to human-readable labels. */
const FEATURE_LABELS = {
  codes: "QR Codes",
  scans: "Scans",
  members: "Team Members",
  api_access: "API Access",
};

/**
 * PlanLimitModalHost — listens for the global 402 event (dispatched by the
 * axios interceptor in client.js) and shows the UpgradeModal from anywhere.
 *
 * Rendered inside BrowserRouter so the modal's Upgrade button can navigate.
 */
function PlanLimitModalHost() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const onLimit = (e) => setInfo(e.detail);
    window.addEventListener(PLAN_LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(PLAN_LIMIT_EVENT, onLimit);
  }, []);

  if (!info) return null;

  return (
    <UpgradeModal
      feature={FEATURE_LABELS[info.feature] || info.feature || "plan"}
      current={info.current}
      limit={info.limit}
      onClose={() => setInfo(null)}
    />
  );
}

/**
 * App — root component with all client-side routes.
 *
 * Public routes:
 *   /login  → LoginPage
 *
 * Protected routes (redirect to /login when unauthenticated):
 *   /             → Layout → DashboardPage
 *   /codes        → Layout → CodesPage
 *   /codes/:code  → Layout → CodeDetailPage
 */
function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <PlanLimitModalHost />
      <ImpersonationBanner />
      <CookieBanner />
      <Routes>
        {/* /privacy → canonical /legal/privacy (kept for old links/bookmarks) */}
        <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />

        {/* Public marketing site — header + footer shell. "/" sends
            authenticated visitors on to their dashboard via HomeGate.
            Auth pages live here too so they share the site chrome and are
            never orphaned dead-ends. */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomeGate />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/mfa-challenge" element={<MfaChallengePage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/qr-types" element={<QrTypesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/docs" element={<DocsLayout />}>
            <Route index element={<DocsHub />} />
            <Route path=":slug" element={<DocPage />} />
          </Route>
          <Route path="/solutions" element={<SolutionsIndex />} />
          <Route path="/solutions/:slug" element={<SolutionTemplate />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/legal" element={<LegalLayout />}>
            <Route index element={<LegalIndex />} />
            {/* Privacy is the operative GDPR policy, rendered in the legal shell. */}
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path=":slug" element={<LegalPage />} />
          </Route>
          {/* Branded 404 for any unmatched path — rendered in the marketing shell. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Admin panel — separate dark-themed shell, superadmin-gated.
            Mounted with /* so the admin app owns all /admin sub-routes. */}
        <Route path="/admin/*" element={<AdminApp />} />

        {/* Protected — wrapped in auth guard then persistent Layout shell */}
        <Route element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/codes" element={<CodesPage />} />
            <Route path="/codes/:code" element={<CodeDetailPage />} />
            <Route path="/batch" element={<BatchPage />} />
            {/* Personal account settings — share a SettingsLayout sidebar */}
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<AccountSettingsPage />} />
              <Route path="notifications" element={<NotificationPrefsPage />} />
              <Route path="webhooks" element={<WebhooksPage />} />
            </Route>
            {/* Organisational settings — standalone, already in main sidebar */}
            <Route path="/settings/org" element={<OrgSettingsPage />} />
            <Route path="/settings/api-keys" element={<ApiKeysPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/export" element={<ExportPage />} />
            <Route path="/tickets" element={<TicketsPage />} />
            <Route path="/tickets/:id" element={<TicketDetailPage />} />

          </Route>
        </Route>

      </Routes>
    </BrowserRouter>
  );
}

export default App;
